/**
 * Build a news-style game recap from game data.
 * Pure function: (details, lineup, gameData, endedAtIso, options?) -> { headline, body }
 * Used when the user clicks "Generate recap" after ending a game (template fallback if AI fails).
 */

import { normalizeRecapStyle } from './recapStyles';

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function pickHeadlineClassic(us, them, opponent, location) {
  const opp = opponent || 'Opponent';
  const loc = location ? ` at ${location}` : '';
  const diff = us - them;
  if (diff === 0) return `Gladiators battle ${opp} to a tie${loc}`;
  if (diff === 1) return `Gladiators edge ${opp} ${us}-${them}${loc}`;
  if (diff === -1) return `Gladiators fall to ${opp} ${them}-${us}${loc}`;
  if (diff >= 2 && diff <= 4) return `Gladiators top ${opp} ${us}-${them}${loc}`;
  if (diff <= -2 && diff >= -4) return `Gladiators drop game to ${opp} ${them}-${us}${loc}`;
  if (diff > 4) return `Gladiators roll past ${opp} ${us}-${them}${loc}`;
  return `Gladiators fall to ${opp} ${them}-${us}${loc}`;
}

function pickHeadlineByStyle(us, them, opponent, location, style) {
  const opp = opponent || 'Opponent';
  const loc = location ? ` at ${location}` : '';
  const diff = us - them;
  switch (style) {
    case 'wire':
      if (diff === 0) return `Gladiators ${us}, ${opp} ${them}${loc ? ` — ${location}` : ''}`;
      return `Gladiators ${us}-${them} ${opp}${loc ? `, ${location}` : ''}`;
    case 'hometownRadio':
      if (diff > 0) return `What a night: Gladiators top ${opp} ${us}-${them}${loc ? ` at ${location}` : ''}`;
      if (diff < 0) return `Tough one at home: ${opp} ${them}, Gladiators ${us}${loc ? ` (${location})` : ''}`;
      return `Gladiators and ${opp} skate to a ${us}-${them} draw${loc ? ` — ${location}` : ''}`;
    case 'notebook':
      return `ICE SHEET | GCG ${us} · ${opp} ${them}${loc ? ` · ${location}` : ''}`;
    case 'tabloidSafe':
      if (diff >= 3) return `Gladiators pour it on in ${us}-${them} win over ${opp}`;
      if (diff <= -3) return `${opp} edge Gladiators ${them}-${us} in hard-fought tilt`;
      if (diff === 0) return `Deadlock drama: Gladiators, ${opp} finish ${us}-${them}`;
      if (diff > 0) return `Gladiators nudge past ${opp} ${us}-${them}`;
      return `Gladiators edged by ${opp} ${them}-${us}`;
    case 'classic':
    default:
      return pickHeadlineClassic(us, them, opponent, location);
  }
}

function buildLead(style, formattedDate, opponent, location, us, them) {
  const locPhrase = location ? (style === 'wire' ? ` (${location})` : ` at ${location}`) : '';
  switch (style) {
    case 'wire':
      return formattedDate
        ? `${formattedDate}. ${opponent}${locPhrase}. Final: Gladiators ${us}, ${opponent} ${them}.`
        : `${opponent}${locPhrase}. Final: Gladiators ${us}, ${opponent} ${them}.`;
    case 'hometownRadio':
      return formattedDate
        ? `If you were at the rink ${formattedDate}, you saw the Gladiators take on ${opponent}${location ? ` at ${location}` : ''}. When the horn sounded: Gladiators ${us}, ${opponent} ${them}.`
        : `The Gladiators welcomed ${opponent}${location ? ` to ${location}` : ''}. When the dust settled: Gladiators ${us}, ${opponent} ${them}.`;
    case 'notebook':
      return formattedDate
        ? `DATE: ${formattedDate}\nOPPONENT: ${opponent}${location ? `\nRINK: ${location}` : ''}\nFINAL: GCG ${us} — ${opponent} ${them}`
        : `OPPONENT: ${opponent}${location ? ` · ${location}` : ''}\nFINAL: GCG ${us} — ${opponent} ${them}`;
    case 'tabloidSafe':
      return formattedDate
        ? `${formattedDate}: The Gladiators and ${opponent} delivered a game worth talking about${location ? ` in ${location}` : ''}. Final scoreboard lights read Gladiators ${us}, ${opponent} ${them}.`
        : `The Gladiators vs. ${opponent}${location ? ` at ${location}` : ''}—final: ${us}-${them}.`;
    case 'classic':
    default:
      return formattedDate
        ? `On ${formattedDate}, the Glass City Gladiators faced off against ${opponent}${location ? ` at ${location}` : ''}. Final score: Gladiators ${us}, ${opponent} ${them}.`
        : `The Glass City Gladiators took on ${opponent}${location ? ` at ${location}` : ''}. Final score: Gladiators ${us}, ${opponent} ${them}.`;
  }
}

function goalSentence(style, scorer, periodLabel, assistStr) {
  const base = `${scorer || 'A Gladiator'} scored in ${periodLabel}${assistStr}`;
  switch (style) {
    case 'wire': {
      const assists = assistStr
        .replace(/^\s*\(assists:\s*/i, '')
        .replace(/\)\s*$/, '')
        .trim();
      const tail = assists ? `. Assists: ${assists}.` : '.';
      return `${periodLabel}: ${scorer || 'Gladiator'} goal${tail}`;
    }
    case 'hometownRadio':
      return `The building popped when ${scorer || 'a Gladiator'} buried one in ${periodLabel}${assistStr}.`;
    case 'notebook':
      return `LAMP LIGHTS · ${periodLabel} · ${scorer || 'GCG'}${assistStr}`;
    case 'tabloidSafe':
      return `${scorer || 'A Gladiator'} strikes in ${periodLabel}${assistStr}—another highlight for the scrapbook.`;
    case 'classic':
    default:
      return base;
  }
}

function notablesBlock(style, hatTricks, multiPoint, goalieStats) {
  const notables = [];
  hatTricks.forEach((p) => {
    if (style === 'wire') notables.push(`${p.name}: ${p.goals || 0} goals (hat trick).`);
    else if (style === 'notebook') notables.push(`HAT TRICK · ${p.name} · ${p.goals || 0}G`);
    else if (style === 'hometownRadio') notables.push(`${p.name} went off for a hat trick—${p.goals || 0} goals—and the bench loved it.`);
    else if (style === 'tabloidSafe') notables.push(`Three-spot hero: ${p.name} with a hat trick (${p.goals || 0} goals).`);
    else notables.push(`${p.name} had a hat trick (${p.goals || 0} goals).`);
  });
  multiPoint.slice(0, 3).forEach((p) => {
    if (style === 'wire') notables.push(`${p.name}: ${p.points || 0} points.`);
    else if (style === 'notebook') notables.push(`MULTI-POINT · ${p.name} · ${p.points || 0}P`);
    else if (style === 'hometownRadio')
      notables.push(`${p.name} stacked ${p.points || 0} points (${p.goals || 0}G, ${p.assists || 0}A)—quietly huge.`);
    else if (style === 'tabloidSafe')
      notables.push(`${p.name} filled the scoresheet: ${p.points || 0} points (${p.goals || 0}G, ${p.assists || 0}A).`);
    else notables.push(`${p.name} had ${p.points || 0} points (${p.goals || 0}G, ${p.assists || 0}A).`);
  });
  goalieStats.forEach((p) => {
    const saves = p.saves || 0;
    const ga = p.goalsAllowed || 0;
    const total = saves + ga;
    const pct = total > 0 ? ((saves / total) * 100).toFixed(1) : '0';
    if (style === 'wire') notables.push(`${p.name}: ${saves} saves / ${total} SA (${pct}%).`);
    else if (style === 'notebook') notables.push(`NET · ${p.name} · ${saves} SV · ${pct}%`);
    else if (style === 'hometownRadio')
      notables.push(`Between the pipes, ${p.name} stood tall—${saves} saves on ${total} shots (${pct}%).`);
    else if (style === 'tabloidSafe')
      notables.push(`${p.name} turned away ${saves} shots (${pct}% save clip) with ${ga} getting through.`);
    else notables.push(`In net, ${p.name} made ${saves} saves on ${total} shots (${pct}% save percentage).`);
  });
  return notables;
}

function penaltyLine(style, count) {
  if (count <= 0) return null;
  if (style === 'wire') return `Penalties: Gladiators ${count}.`;
  if (style === 'notebook') return `PIM NIGHT · ${count} Gladiator penalties`;
  if (style === 'hometownRadio')
    return count <= 10
      ? `Discipline note: ${count} Gladiator penalties—plenty to clean up next skate.`
      : `Chippy night—${count} penalties. Coaches will have film room fodder.`;
  if (style === 'tabloidSafe')
    return count > 10
      ? `Penalty box got a workout: ${count} whistles against the Gladiators.`
      : `${count} penalty(ies) on the Gladiators’ card.`;
  if (count <= 10) return `The Gladiators took ${count} penalty(ies) on the night.`;
  return `A physical game saw the Gladiators take ${count} penalties.`;
}

/**
 * @param {Object} details - { opponent, date, location }
 * @param {Array} lineup - players with goals, assists, saves, goalsAllowed, isGoalie
 * @param {Object} gameData - { score: { us, them }, shots, goalieSaves, gameLog }
 * @param {string} endedAtIso - ISO date string
 * @param {{ recapStyle?: string }} [options]
 * @returns {{ headline: string, body: string }}
 */
export function buildGameRecap(details, lineup, gameData, endedAtIso, options = {}) {
  const style = normalizeRecapStyle(options.recapStyle);
  const detailsObj = details || {};
  const opponent = (detailsObj.opponent || '').trim() || 'Opponent';
  const location = (detailsObj.location || '').trim() || '';
  const dateStr = detailsObj.date || '';
  const lineupArr = Array.isArray(lineup) ? lineup : [];
  const gd = gameData || {};
  const score = gd.score || { us: 0, them: 0 };
  const us = score.us ?? 0;
  const them = score.them ?? 0;
  const gameLog = Array.isArray(gd.gameLog) ? gd.gameLog : [];

  const headline = pickHeadlineByStyle(us, them, opponent, location, style);
  const formattedDate = formatDate(dateStr);

  const paragraphs = [];

  paragraphs.push(buildLead(style, formattedDate, opponent, location, us, them));

  const ourGoals = gameLog.filter((e) => e.type === 'goal_us');
  if (ourGoals.length > 0) {
    const highlights = [];
    ourGoals.forEach((e) => {
      const period = e.period <= 3 ? `Period ${e.period}` : 'Overtime';
      const assists = [e.assist1, e.assist2, e.assist3].filter(Boolean);
      const assistStr = assists.length > 0 ? ` (assists: ${assists.join(', ')})` : '';
      highlights.push(goalSentence(style, e.scorer, period, assistStr));
    });
    const joiner = style === 'notebook' ? '\n' : ' ';
    paragraphs.push(highlights.join(joiner));
  }

  const skaters = lineupArr.filter((p) => p && !p.isGoalie);
  const goalies = lineupArr.filter((p) => p && p.isGoalie);
  const hatTricks = skaters.filter((p) => (p.goals || 0) >= 3);
  const multiPoint = skaters.filter((p) => (p.points || 0) >= 2 && (p.goals || 0) < 3);
  const goalieStats = goalies.filter((p) => (p.saves || 0) > 0 || (p.goalsAllowed || 0) > 0);

  if (hatTricks.length > 0 || multiPoint.length > 0 || goalieStats.length > 0) {
    const notables = notablesBlock(style, hatTricks, multiPoint, goalieStats);
    if (notables.length > 0) paragraphs.push(notables.join(style === 'notebook' ? '\n' : ' '));
  }

  const penalties = gameLog.filter((e) => e.type === 'penalty');
  const penLine = penaltyLine(style, penalties.length);
  if (penLine) paragraphs.push(penLine);

  const fanQuote = options.fanQuote;
  if (fanQuote && fanQuote.displayName && fanQuote.text) {
    paragraphs.push(
      `From the crowd — ${String(fanQuote.displayName).trim()}: “${String(fanQuote.text).trim()}”`
    );
  }

  const body = paragraphs.join('\n\n').trim() || `Final: Gladiators ${us}, ${opponent} ${them}.`;
  return { headline, body };
}
